import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import { CustomDomain, WebsiteHeroBanner } from '@baboor/baboor-website-domain'
import { ApiResponse } from '@baboor/common-models'
import { Observable } from 'rxjs'

export interface CreateBannerDto {
    imageUrl: string
    title: string
    description?: string | null
    ctaText?: string | null
    ctaUrl?: string | null
    sortOrder?: number
    isActive?: boolean
}

export interface UpdateBannerDto {
    imageUrl?: string
    title?: string
    description?: string | null
    ctaText?: string | null
    ctaUrl?: string | null
    sortOrder?: number
    isActive?: boolean
}

export interface WebsiteSettings {
    configured?: boolean
    id?: string
    organizationId?: string
    slug: string
    websiteName?: string | null
    logoUrl?: string | null
    logoSize?: string | null
    logoRounding?: string | null
    logoFit?: string | null
    faviconUrl?: string | null
    coverImageUrl?: string | null
    primaryColor?: string | null
    secondaryColor?: string | null
    status?: string
    themeId?: string | null
    showAboutSection?: boolean
    showServicesSection?: boolean
    showTeamSection?: boolean
    showGallerySection?: boolean
    showLookbookSection?: boolean
    showPromotionsSection?: boolean
    showContactSection?: boolean
    metaTitle?: string | null
    metaDescription?: string | null
    metaKeywords?: string | null
    ogImageUrl?: string | null
    phone?: string | null
    email?: string | null
    address?: string | null
    businessHours?: Record<string, unknown> | null
    tagline?: string | null
    aboutDescription?: string | null
    aboutYearsExperience?: string | null
    aboutWhyChooseUs?: string[] | null
    theme?: string
    socialLinks?: Array<{ id: string; platform: string; url: string }>
}

export interface CreateWebsiteSettings {
    websiteName: string
    slug: string
    logoUrl?: string
    logoSize?: string
    logoRounding?: string
    logoFit?: string
    faviconUrl?: string
    coverImageUrl?: string
    primaryColor?: string
    secondaryColor?: string
    tagline?: string
}

export interface WebsiteInquiry {
    id: string
    name: string
    email: string
    phone: string | null
    message: string
    isRead: boolean
    createdAt: string
}

export interface WebsiteTheme {
    id: string
    name: string
    description: string | null
    previewImageUrl: string | null
    isPremium: boolean
    config: Record<string, unknown> | null
}

export interface UpdateWebsiteSettings {
    websiteName?: string
    slug?: string
    logoUrl?: string
    logoSize?: string
    logoRounding?: string
    logoFit?: string
    faviconUrl?: string | null
    coverImageUrl?: string
    primaryColor?: string
    secondaryColor?: string
    tagline?: string
    aboutDescription?: string
    aboutYearsExperience?: string
    aboutWhyChooseUs?: string[]
    phone?: string
    email?: string
    address?: string
    businessHours?: Array<{
        day: string
        open: string
        close: string
        isClosed?: boolean
    }>
    socialLinks?: Array<{ platform: string; url: string }>
    showAboutSection?: boolean
    showServicesSection?: boolean
    showTeamSection?: boolean
    showGallerySection?: boolean
    showLookbookSection?: boolean
    showPromotionsSection?: boolean
    showContactSection?: boolean
    metaTitle?: string
    metaDescription?: string
    metaKeywords?: string
    ogImageUrl?: string
    theme?: string
}

@Injectable({ providedIn: 'root' })
export class WebsiteApiService {
    private baseUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.baseUrl = `${env.apiUrl}/v1/websites`
    }

    getSettings(): Observable<ApiResponse<WebsiteSettings>> {
        return this.http.get<ApiResponse<WebsiteSettings>>(`${this.baseUrl}/me`)
    }

    createSettings(
        data: CreateWebsiteSettings,
    ): Observable<ApiResponse<WebsiteSettings>> {
        return this.http.post<ApiResponse<WebsiteSettings>>(this.baseUrl, data)
    }

    updateSettings(
        data: UpdateWebsiteSettings,
    ): Observable<ApiResponse<WebsiteSettings>> {
        return this.http.put<ApiResponse<WebsiteSettings>>(
            `${this.baseUrl}/me`,
            data,
        )
    }

    publish(
        status: 'draft' | 'preview' | 'published' | 'unpublished',
    ): Observable<ApiResponse<WebsiteSettings>> {
        return this.http.put<ApiResponse<WebsiteSettings>>(
            `${this.baseUrl}/me/publish`,
            { status },
        )
    }

    getThemes(): Observable<ApiResponse<WebsiteTheme[]>> {
        return this.http.get<ApiResponse<WebsiteTheme[]>>(
            `${this.baseUrl}/themes`,
        )
    }

    setTheme(themeId: string): Observable<ApiResponse<WebsiteSettings>> {
        return this.http.put<ApiResponse<WebsiteSettings>>(
            `${this.baseUrl}/me/themes/${themeId}`,
            {},
        )
    }

    getInquiries(): Observable<ApiResponse<WebsiteInquiry[]>> {
        return this.http.get<ApiResponse<WebsiteInquiry[]>>(
            `${this.baseUrl}/me/inquiries`,
        )
    }

    getDomains(): Observable<ApiResponse<CustomDomain[]>> {
        return this.http.get<ApiResponse<CustomDomain[]>>(
            `${this.baseUrl}/me/domains`,
        )
    }

    addDomain(domain: string): Observable<ApiResponse<CustomDomain>> {
        return this.http.post<ApiResponse<CustomDomain>>(
            `${this.baseUrl}/me/domains`,
            { domain },
        )
    }

    verifyDomain(id: string): Observable<ApiResponse<CustomDomain>> {
        return this.http.post<ApiResponse<CustomDomain>>(
            `${this.baseUrl}/me/domains/${id}/verify`,
            {},
        )
    }

    removeDomain(id: string): Observable<ApiResponse<void>> {
        return this.http.delete<ApiResponse<void>>(
            `${this.baseUrl}/me/domains/${id}`,
        )
    }

    getBanners(): Observable<ApiResponse<WebsiteHeroBanner[]>> {
        return this.http.get<ApiResponse<WebsiteHeroBanner[]>>(
            `${this.baseUrl}/me/banners`,
        )
    }

    createBanner(
        data: CreateBannerDto,
    ): Observable<ApiResponse<WebsiteHeroBanner>> {
        return this.http.post<ApiResponse<WebsiteHeroBanner>>(
            `${this.baseUrl}/me/banners`,
            data,
        )
    }

    updateBanner(
        id: string,
        data: UpdateBannerDto,
    ): Observable<ApiResponse<WebsiteHeroBanner>> {
        return this.http.put<ApiResponse<WebsiteHeroBanner>>(
            `${this.baseUrl}/me/banners/${id}`,
            data,
        )
    }

    deleteBanner(id: string): Observable<ApiResponse<{ id: string }>> {
        return this.http.delete<ApiResponse<{ id: string }>>(
            `${this.baseUrl}/me/banners/${id}`,
        )
    }

    reorderBanners(
        bannerIds: string[],
    ): Observable<ApiResponse<WebsiteHeroBanner[]>> {
        return this.http.put<ApiResponse<WebsiteHeroBanner[]>>(
            `${this.baseUrl}/me/banners/reorder`,
            { bannerIds },
        )
    }

    uploadBannerImage(
        file: File,
    ): Observable<ApiResponse<{ imageUrl: string }>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<{ imageUrl: string }>>(
            `${this.baseUrl}/me/banners/upload`,
            formData,
        )
    }

    uploadLogo(file: File): Observable<ApiResponse<{ logoUrl: string }>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<{ logoUrl: string }>>(
            `${this.baseUrl}/me/logo`,
            formData,
        )
    }

    deleteLogo(): Observable<ApiResponse<void>> {
        return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/me/logo`)
    }

    uploadFavicon(file: File): Observable<ApiResponse<{ faviconUrl: string }>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<{ faviconUrl: string }>>(
            `${this.baseUrl}/me/favicon`,
            formData,
        )
    }

    deleteFavicon(): Observable<ApiResponse<void>> {
        return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/me/favicon`)
    }
}
